import type { TIntegrationCategory } from '~/models/Unified';

// Build/maintain effort per API category, from 1 (simplest) to 4 (hardest).
//
// The first 18 scores are unchanged from the integrations-build-calculator draft
// (unified-to/unified-www4#219). They track how fragmented and demanding each category's vendor
// APIs are, not how many objects the unified model has (HRIS is 1, ATS and accounting are 4).
// The scores marked "added" were assigned later on the same scale, by comparison with those
// anchors; review them against real build experience.
//
// Every API category needs an entry: the Record type fails the build if a new category has none.
type TScoredCategory = Exclude<TIntegrationCategory, 'passthrough' | 'metadata' | 'scim' | 'saml'>;

export const integrationEffort: Record<TScoredCategory, { build: number; maintain: number }> = {
    hris: { build: 1, maintain: 1 },
    ats: { build: 4, maintain: 4 },
    auth: { build: 2, maintain: 2 },
    crm: { build: 3, maintain: 3 },
    enrich: { build: 1, maintain: 1 },
    martech: { build: 2, maintain: 2 },
    ticketing: { build: 2, maintain: 2 },
    uc: { build: 2, maintain: 2 },
    accounting: { build: 4, maintain: 4 },
    storage: { build: 2, maintain: 2 },
    commerce: { build: 4, maintain: 4 },
    payment: { build: 3, maintain: 3 },
    genai: { build: 1, maintain: 1 },
    messaging: { build: 3, maintain: 3 },
    kms: { build: 3, maintain: 3 },
    task: { build: 2, maintain: 2 },
    lms: { build: 3, maintain: 3 },
    repo: { build: 2, maintain: 2 },

    // added: few objects, simple read-mostly APIs (like enrich, genai)
    forms: { build: 1, maintain: 1 },
    // added: moderate surfaces with one or two core objects (like task, ticketing, storage)
    verification: { build: 2, maintain: 2 },
    signing: { build: 2, maintain: 2 },
    clubs: { build: 2, maintain: 2 },
    datastore: { build: 2, maintain: 2 },
    analytics: { build: 2, maintain: 2 },
    performance: { build: 2, maintain: 2 },
    // added: recurrence/timezones, rates and labels, partner programs, platform API approvals,
    // fragmented niche vendors or many provider-specific resources (like crm, messaging, payment)
    calendar: { build: 3, maintain: 3 },
    shipping: { build: 3, maintain: 3 },
    assessment: { build: 3, maintain: 3 },
    cdp: { build: 3, maintain: 3 },
    social: { build: 3, maintain: 3 },
    legal: { build: 3, maintain: 3 },
    compute: { build: 3, maintain: 3 },
    property: { build: 3, maintain: 3 },
    // added: large, heavily gated surfaces (developer-token approval for ads; FHIR plus HIPAA
    // compliance for healthcare), like accounting and commerce
    ads: { build: 4, maintain: 4 },
    healthcare: { build: 4, maintain: 4 },
};

export type BuildIntegrationCategory = keyof typeof integrationEffort;

export const BUILD_CATEGORIES = Object.keys(integrationEffort) as BuildIntegrationCategory[];

export function getComplexityFactors(categories: BuildIntegrationCategory[], effort: 'build' | 'maintain') {
    const uniqueCategories = [...new Set(categories)];
    return uniqueCategories.map((category) => integrationEffort[category][effort]);
}

export type EffortType = 'build' | 'maintain';

// Computes a weighted complexity factor in the range [1.0, ~2.0].
// For now we treat each selected category as count = 1 (no per-category counts provided by UI yet).
export function computeWeightedComplexity(
    categories: BuildIntegrationCategory[],
    effort: EffortType
): number {
    const factors = getComplexityFactors(categories, effort);
    const total = factors.length;
    if (total === 0) {
        return 1.0;
    }
    const sum = factors.reduce((acc, score) => acc + score, 0);
    const average = sum / total; // between 1 and 4
    // Base factor starts at 1 and scales with average complexity
    return 1 + 0.25 * average;
}

export type DataFrequency = 'batch' | 'near-real-time' | 'real-time';